import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PdfButton from "../../ui/PdfButton.jsx";
import StickyActionBar from "../../ui/StickyActionBar.jsx";
import {
  EMPTY_BOARD, bucketsByGroup, fmtQty, hasMixedProcesses, loadCoatingBoard, loadServiceVendors, manifestBuckets,
  matchingDrafts, pruneSelection, receiveRowsFor, selectionVendorId, stagingGroups, tickedPallets, toggleGroupIds,
  togglePalletIds, toggleTaskIds,
} from "../../../features/outsourcing/coatingData";
import {
  addToDraft, beaconsBackOn, createFlowGuard, createManifest, createPalletManifest, phoneCoatingUi,
  printPalletLabels, receiveManifest, removeLine, sendManifest,
} from "../../../features/outsourcing/coatingActions";
import { findPallets, showPallets } from "../../../features/pallets/opoPalletFlows";
import ToSendList from "./outsourcing/ToSendList.jsx";
import { AtVendorCard, DraftCard, HistoryCard } from "./outsourcing/ManifestCards.jsx";
import { CreatePaperPanel, ReceivePanel } from "./outsourcing/PaperPanels.jsx";
import "../../mobile.css";
import "./outsourcing.css";

// The Shipping hub's Outsourcing tab on a phone (2026-10-29): the coating desk in operator mode
// (pages/CoatingManagementPage mode="operator", as the desktop hub mounts it). To send (pallet first: one tick per
// pallet, "Make PLT-x's paper", Find / Find all), Loading (Draft papers: Send through the re-check and the pallet
// checklist, the paper, pallet labels at the label station, Find pallets), At vendor (Receive back, Beacons back
// on), History (the paper again). Every read, rule and write is the desktop's (features/outsourcing/coatingData +
// coatingActions); every "who" is the Working-as session (`ui` = the phone Shipping screen's). The office's tools
// (vendor email, invoice entry, Delete draft, invoice # / cost / freight on a receive, sending without the
// checklist) stay on the desktop. Layout only.

const TABS = [
  { id: "send", label: "To send" },
  { id: "loading", label: "Loading" },
  { id: "vendor", label: "At vendor" },
  { id: "history", label: "History" },
];
const EMPTY_FORM = { vendorId: "", dueBack: "", notes: "", draftId: "" };

export default function OutsourcingTab({ apiBase, ui }) {
  const cui = useMemo(() => phoneCoatingUi(ui), [ui]);
  const [tab, setTab] = useState("send");
  const [board, setBoard] = useState(EMPTY_BOARD);
  const [opos, setOpos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [message, setMessage] = useState(null); // {text, ok}
  const [paperOffer, setPaperOffer] = useState(null); // {url, filename, opoNumber} after a send
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [receiveOpo, setReceiveOpo] = useState(null);
  const [receiveRows, setReceiveRows] = useState({});
  // one pallet flow (send / find / beacons back on) at a time, as on the desktop
  const runFlow = useRef(createFlowGuard()).current;
  const readSeq = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++readSeq.current;
    setLoading(true);
    setLoadError("");
    try {
      const r = await loadCoatingBoard(apiBase);
      if (seq !== readSeq.current) return;
      setBoard(r.board);
      setOpos(r.opos);
      setSelected((prev) => pruneSelection(prev, r.board.staging));
    } catch (err) {
      if (seq === readSeq.current) setLoadError(err.message || "Failed to load the coating board.");
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    let alive = true;
    loadServiceVendors(apiBase).then((v) => { if (alive) setVendors(v); });
    return () => { alive = false; };
  }, [apiBase]);

  const groups = useMemo(() => stagingGroups(board.staging), [board.staging]);
  const buckets = useMemo(() => bucketsByGroup(groups), [groups]);
  const mixed = useMemo(() => hasMixedProcesses(groups), [groups]);
  const { drafts, atVendor, history } = useMemo(() => manifestBuckets(opos), [opos]);
  const selectedTasks = useMemo(() => {
    const byId = new Map((board.staging || []).map((t) => [t.task_id, t]));
    return [...selected].map((id) => byId.get(id)).filter(Boolean);
  }, [selected, board.staging]);
  const defaultVendorId = useMemo(() => selectionVendorId(selectedTasks), [selectedTasks]);
  const joinable = useMemo(() => matchingDrafts(drafts, selectedTasks), [drafts, selectedTasks]);
  const ticked = useMemo(() => tickedPallets(groups, buckets, selected), [groups, buckets, selected]);
  const tickedText = ticked.map((p) => `${p.short_id} ${fmtQty(p.total)}`).join(" · ");

  const counts = { send: board.counts?.staging ?? board.staging.length, loading: drafts.length, vendor: atVendor.length, history: history.length };

  const say = (text, ok = true) => setMessage(text ? { text, ok } : null);
  const openTab = (id) => { setTab(id); setMessage(null); setPaperOffer(null); };

  // --- To send ---
  const applySelection = async (r) => {
    if (r.refuse) await cui.alert(r.refuse);
    else setSelected(r.ids);
  };
  const tickPallet = (g, pallets, key, checked) => applySelection(togglePalletIds(selected, selectedTasks, g, pallets, key, checked));
  const toggleTask = (t) => applySelection(toggleTaskIds(selected, selectedTasks, t));
  const toggleGroup = (g, checked) => applySelection(toggleGroupIds(selected, selectedTasks, g, checked));
  const find = (ids, title) => runFlow(() => findPallets(ids, title));

  const openCreate = () => {
    setForm({
      vendorId: defaultVendorId != null ? String(defaultVendorId) : "", dueBack: "", notes: "",
      draftId: joinable.length ? String(joinable[0].opo_id) : "",
    });
    setCreateOpen(true);
  };
  const afterPaper = async () => {
    setCreateOpen(false);
    setSelected(new Set());
    await reload();
    setTab("loading");
  };
  const create = () => createManifest({
    apiBase, cui, taskIds: [...selected], vendorId: form.vendorId, dueBack: form.dueBack, notes: form.notes,
    setBusy, onDone: afterPaper,
  });
  const addToPaper = () => addToDraft({
    apiBase, cui, target: joinable.find((o) => String(o.opo_id) === form.draftId), taskIds: [...selected],
    setBusy, onDone: afterPaper,
  });
  const makePalletPaper = (shortId) => createPalletManifest({ apiBase, cui, palletShortId: shortId, setBusy, onDone: afterPaper });

  // --- Loading ---
  const send = (opo) => runFlow(async () => {
    setMessage(null); setPaperOffer(null);
    const r = await sendManifest({
      apiBase, cui, opo, operator: true, reload, setBusy,
      onDone: async () => { await reload(); setTab("vendor"); },
    });
    if (r && r.sent && r.paper) setPaperOffer({ ...r.paper, opoNumber: opo.opo_number });
  });
  const takeOff = (opo, line) => removeLine({ apiBase, cui, opo, line, setBusy, onDone: reload });
  const palletLabels = async (opo) => {
    setMessage(null);
    setBusy(true);
    try {
      const r = await printPalletLabels({ apiBase, cui, opo });
      if (r.message) say(r.message);
    } finally {
      setBusy(false);
    }
  };
  const findOnPaper = (opo) => runFlow(() => showPallets(opo.opo_id, "to_vendor"));

  // --- At vendor ---
  const openReceive = (opo) => { setReceiveRows(receiveRowsFor(opo)); setReceiveOpo(opo); };
  const receive = () => receiveManifest({
    apiBase, cui, opo: receiveOpo, rows: receiveRows, office: false, setBusy,
    onDone: async () => { setReceiveOpo(null); await reload(); },
  });
  const backOn = (opo) => runFlow(() => beaconsBackOn({ cui, opo }));

  const showBar = tab === "send" && selected.size > 0 && !createOpen;
  return (
    <div className={`jq-out-root${showBar ? " with-bar" : ""}`} data-testid="phone-outsourcing">
      <div className="jq-out-seg" role="tablist" aria-label="Outsourcing">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? "active" : ""}
            onClick={() => openTab(t.id)}>
            {t.label}{counts[t.id] > 0 && <span className="jq-queue-count">{counts[t.id]}</span>}
          </button>
        ))}
        <button type="button" aria-label="Refresh" disabled={loading} onClick={reload} style={{ flex: "0 0 44px", padding: 0 }}>
          <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
        </button>
      </div>

      {message && (
        <div className={`jq-note ${message.ok ? "jq-note-info" : "jq-note-danger"} d-flex justify-content-between align-items-start`} role="status">
          <span>{message.text}</span>
          <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setMessage(null)} />
        </div>
      )}
      {paperOffer && (
        <div className="jq-note jq-note-info" data-testid="phone-out-paper-offer">
          <div className="mb-2">{paperOffer.opoNumber} is sent. Print the paper that travels with the parts:</div>
          <PdfButton url={paperOffer.url} filename={paperOffer.filename} label={`The paper (${paperOffer.opoNumber})`} fetchNow
            className="btn btn-primary w-100" testId="phone-out-sent-paper" />
        </div>
      )}
      {loadError && (
        <div className="jq-note jq-note-danger" role="alert">
          Couldn't load outsourcing: {loadError}
          <button type="button" className="btn btn-outline-danger w-100 mt-2" onClick={reload}>Try again</button>
        </div>
      )}
      {loading && !board.staging.length && !opos.length ? (
        <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>
      ) : (
        <>
          {showBar && tickedText ? (
            <div className="jq-note jq-note-info jq-out-ticked" data-testid="phone-out-ticked-pallets">On the paper: {tickedText}</div>
          ) : null}
          {tab === "send" && (
            <ToSendList groups={groups} buckets={buckets} selected={selected} busy={busy} mixed={mixed}
              inProduction={board.in_production || []} onTickPallet={tickPallet} onToggleTask={toggleTask}
              onToggleGroup={toggleGroup} onFind={find} onMakePaper={makePalletPaper} />
          )}
          {tab === "loading" && (drafts.length ? drafts.map((o) => (
            <DraftCard key={o.opo_id} apiBase={apiBase} opo={o} busy={busy} onSend={send} onRemoveLine={takeOff}
              onPalletLabels={palletLabels} onFindPallets={findOnPaper} />
          )) : <div className="jq-list-state">No papers being loaded. Tick pallets on To send and make the paper.</div>)}
          {tab === "vendor" && (atVendor.length ? atVendor.map((o) => (
            <AtVendorCard key={o.opo_id} apiBase={apiBase} opo={o} busy={busy} onReceive={openReceive}
              onPalletLabels={palletLabels} onBeaconsBackOn={backOn} />
          )) : <div className="jq-list-state">Nothing is out at a vendor right now.</div>)}
          {tab === "history" && (history.length ? history.map((o) => (
            <HistoryCard key={o.opo_id} apiBase={apiBase} opo={o} />
          )) : <div className="jq-list-state">No returned, closed or cancelled papers yet.</div>)}
        </>
      )}

      {showBar && (
        <StickyActionBar testId="phone-out-batch-bar">
          <button type="button" className="btn btn-outline-secondary" onClick={() => setSelected(new Set())}>Clear</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={openCreate}>
            Make the paper ({selected.size})
          </button>
        </StickyActionBar>
      )}

      <CreatePaperPanel open={createOpen} count={selected.size} tickedText={tickedText} form={form} setForm={setForm}
        vendors={vendors} defaultVendorId={defaultVendorId} selectedTasks={selectedTasks} drafts={joinable} busy={busy}
        onCreate={create} onAddToDraft={addToPaper} onClose={() => setCreateOpen(false)} />
      <ReceivePanel opo={receiveOpo} rows={receiveRows} setRows={setReceiveRows} busy={busy} onReceive={receive}
        onClose={() => setReceiveOpo(null)} />
    </div>
  );
}
